
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import type { Application } from '@/types/database.types';
import type { AdditionalContact } from '@/types/database/application-types';
import { PrimaryContactForm } from './contact/PrimaryContactForm';
import { AdditionalContactForm } from './contact/AdditionalContactForm';

interface ContactInformationProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const ContactInformation = ({ data, onSave }: ContactInformationProps) => {
  const [additionalContacts, setAdditionalContacts] = useState<AdditionalContact[]>(
    Array.isArray(data.additional_contacts) ? data.additional_contacts : []
  );
  const [formInitialized, setFormInitialized] = useState(false);

  const { register, handleSubmit, reset, setValue, watch, getValues } = useForm<Partial<Application>>({
    defaultValues: {
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      title: data.title || '',
      phone: data.phone || '',
      email: data.email || '',
      company_legal_name: data.company_legal_name || '',
      company_website: data.company_website || '',
      additional_contacts: data.additional_contacts || [],
      // Cast to any to handle the missing type definition
      ...(data as any).has_signing_authority !== undefined ? { has_signing_authority: Boolean((data as any).has_signing_authority) } : { has_signing_authority: false },
    },
  });

  // Watch for signing authority changes
  const hasSigningAuthority = watch('has_signing_authority' as any);

  // Update the form values when external data changes, but only once on initialization
  useEffect(() => {
    if (!data || formInitialized) return;
    
    const initialValues = {
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      title: data.title || '',
      phone: data.phone || '',
      email: data.email || '',
      company_legal_name: data.company_legal_name || '',
      company_website: data.company_website || '',
      additional_contacts: data.additional_contacts || [],
      // Cast to any to handle the missing type definition
      ...(data as any).has_signing_authority !== undefined ? { has_signing_authority: Boolean((data as any).has_signing_authority) } : { has_signing_authority: false },
    };
    
    reset(initialValues);
    setAdditionalContacts(Array.isArray(data.additional_contacts) ? data.additional_contacts : []);
    setFormInitialized(true);
  }, [data, reset, formInitialized]);

  // Handle form submission
  const onSubmit = handleSubmit((formData) => {
    onSave(formData);
  });

  // Manual save handler for blur events
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Only save on blur, not on every keystroke
    const formValues = getValues();
    onSave(formValues);
  };

  // Watch for changes to company related fields and trigger save
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'company_legal_name' || name === 'company_website') {
        // Save after a brief delay to prevent excessive saving
        setTimeout(() => {
          onSubmit();
        }, 500);
      }
    });
    
    return () => subscription.unsubscribe();
  }, [watch, onSubmit]);

  // Handle signing authority change
  const handleSigningAuthorityChange = (checked: boolean) => {
    setValue('has_signing_authority' as any, checked);
    // Trigger save after change
    setTimeout(() => {
      const event = new Event('blur') as any;
      handleInputBlur(event);
    }, 100);
  };

  const addContact = () => {
    if (additionalContacts.length < 5) {
      const newContact: AdditionalContact = {
        first_name: '',
        last_name: '',
        title: '',
        phone: '',
        email: '',
      };
      
      // Add contact safely
      const updatedContacts = [...additionalContacts, newContact];
      setAdditionalContacts(updatedContacts);
      setValue('additional_contacts', updatedContacts);
      
      // Save after adding
      onSubmit();
    }
  };

  const removeContact = (index: number) => {
    const updatedContacts = additionalContacts.filter((_, i) => i !== index);
    setAdditionalContacts(updatedContacts);
    setValue('additional_contacts', updatedContacts);
    
    // Save after removing
    onSubmit();
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-3 pb-2 border-b border-gray-100">
            <h2 className="text-2xl font-semibold">Contact Information</h2>
          </div>
          
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/10 flex items-start gap-3">
            <p className="text-muted-foreground text-sm">
              Complete with your contact information first, as well any other points of contact who need to be involved in the evaluation, signing, and onboarding process for the organization.
            </p>
          </div>

          <PrimaryContactForm 
            register={register} 
            setValue={setValue}
            watch={watch}
            onBlur={handleInputBlur}
          />

          {additionalContacts.length > 0 && (
            <div className="space-y-6">
              {additionalContacts.map((_, index) => (
                <AdditionalContactForm
                  key={index}
                  index={index}
                  register={register}
                  onRemove={() => removeContact(index)}
                  onBlur={handleInputBlur}
                />
              ))}
            </div>
          )}

          <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <Switch
              id="has_signing_authority"
              checked={Boolean(hasSigningAuthority) || false}
              onCheckedChange={handleSigningAuthorityChange}
            />
            <Label htmlFor="has_signing_authority" className="text-sm font-medium cursor-pointer">
              Has Authority to Sign Contracts for the Organization
            </Label>
          </div>

          <div className="space-y-4">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={addContact}
              disabled={additionalContacts.length >= 5}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Contact
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
